/**
 * Menu item form logic, shared by creating and editing.
 *
 * One model for both so the two paths can't validate differently — a dish created through
 * "Add" must be exactly as valid as one produced by editing. Pure, so the rules are tested
 * without rendering.
 */
import type { MenuCategory, MenuItem, NewMenuItem } from "@glowin/schemas";
import { parsePriceToMinor } from "./format.js";

export interface ItemForm {
  en: string;
  zh: string;
  description: string;
  /** As typed, e.g. "18.99". Kept as text so a half-typed price isn't destroyed. */
  priceText: string;
  categoryId: string;
  available: boolean;
  /** Attached option groups, by id. */
  groupIds: string[];
}

/** Currency is per-restaurant and not editable per dish; inherit or default to USD. */
const CURRENCY = "USD";

export function toItemForm(item: MenuItem | undefined, categories: readonly MenuCategory[]): ItemForm {
  if (!item) {
    return {
      en: "",
      zh: "",
      description: "",
      priceText: "",
      // Preselect the first category so a new dish is valid without extra clicks.
      categoryId: categories[0]?.id ?? "",
      available: true,
      groupIds: []
    };
  }
  return {
    en: item.name.en,
    zh: item.name.zh ?? "",
    description: item.description ?? "",
    priceText: (item.basePrice.amountMinor / 100).toFixed(2),
    categoryId: item.categoryId,
    available: item.available,
    groupIds: [...item.groupIds]
  };
}

export type ItemFieldErrors = Partial<Record<"en" | "zh" | "priceText" | "categoryId" | "description", string>>;

export function validateItem(form: ItemForm): ItemFieldErrors {
  const errors: ItemFieldErrors = {};

  if (!form.en.trim()) {
    errors.en = "A name is required — the agent reads it to callers.";
  } else if (form.en.trim().length > 120) {
    errors.en = "Keep it under 120 characters.";
  }
  if (form.zh.trim().length > 120) errors.zh = "Keep it under 120 characters.";
  if (form.description.trim().length > 500) {
    errors.description = "Keep the description under 500 characters.";
  }

  const minor = parsePriceToMinor(form.priceText);
  if (form.priceText.trim() === "") {
    errors.priceText = "A price is required.";
  } else if (minor === undefined) {
    errors.priceText = "Enter a price like 18.99.";
  } else if (minor === 0) {
    // A free dish is almost always a typo, and the agent would quote "zero dollars".
    errors.priceText = "Price must be more than zero.";
  }

  if (!form.categoryId) {
    errors.categoryId = "Choose a category.";
  }

  return errors;
}

export function isValidItem(form: ItemForm): boolean {
  return Object.keys(validateItem(form)).length === 0;
}

/** Body for creating. No itemId or sortIndex — the server assigns both. */
export function toNewItem(form: ItemForm): NewMenuItem {
  const zh = form.zh.trim();
  const description = form.description.trim();
  return {
    name: { en: form.en.trim(), ...(zh ? { zh } : {}) },
    ...(description ? { description } : {}),
    basePrice: { amountMinor: parsePriceToMinor(form.priceText)!, currency: CURRENCY },
    categoryId: form.categoryId,
    available: form.available,
    groupIds: form.groupIds
  };
}

/**
 * Body for updating. Preserves itemId and sortIndex from the existing dish: the id is
 * permanent (the agent uses it) and re-deriving sortIndex would silently reorder the menu.
 */
export function toUpdatedItem(form: ItemForm, existing: MenuItem): MenuItem {
  const zh = form.zh.trim();
  const description = form.description.trim();
  return {
    itemId: existing.itemId,
    sortIndex: existing.sortIndex,
    name: { en: form.en.trim(), ...(zh ? { zh } : {}) },
    ...(description ? { description } : {}),
    basePrice: {
      ...existing.basePrice,
      amountMinor: parsePriceToMinor(form.priceText)!
    },
    categoryId: form.categoryId,
    available: form.available,
    groupIds: form.groupIds
  };
}

export function isItemDirty(form: ItemForm, original: ItemForm): boolean {
  return (
    form.en !== original.en ||
    form.zh !== original.zh ||
    form.description !== original.description ||
    form.priceText !== original.priceText ||
    form.categoryId !== original.categoryId ||
    form.available !== original.available ||
    form.groupIds.length !== original.groupIds.length ||
    form.groupIds.some((g, i) => g !== original.groupIds[i])
  );
}

/** Toggle an option group on or off, preserving order for predictable prompts. */
export function toggleGroup(form: ItemForm, groupId: string): ItemForm {
  const has = form.groupIds.includes(groupId);
  return {
    ...form,
    groupIds: has ? form.groupIds.filter((g) => g !== groupId) : [...form.groupIds, groupId]
  };
}
